export default function SafetySection() {
  const safetyLogos = [
    {
      name: 'eCOGRA',
      logo: '/safety-logos/ecogra.svg',
      description: 'eCOGRA certifies fair gaming and player protection standards.'
    },
    {
      name: 'BeGambleAware',
      logo: '/safety-logos/be-gamble-aware.svg',
      description: 'BeGambleAware provides support tools for safer gambling.'
    },
    {
      name: 'iTech Labs',
      logo: '/safety-logos/itech-labs.svg',
      description: 'iTech Labs independently audits game fairness and RNG systems.'
    },
    {
      name: '18+',
      logo: '/safety-logos/18plus.svg',
      description: 'Gambling is only permitted for adults aged 18 and over.'
    },
    {
      name: 'GLI',
      logo: '/safety-logos/gli.svg',
      description: 'Gaming Laboratories International performs independent compliance testing.'
    },
    {
      name: 'MGA',
      logo: '/safety-logos/mga.svg',
      description: 'Malta Gaming Authority is one of the most respected gambling regulators.'
    },
    {
      name: 'CGA',
      logo: '/safety-logos/cga.png',
      description: 'CGA promotes fair and transparent online gaming standards for player protection.'
    }
  ];

  return (
    <>
      {/* Your safety comes first */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your safety comes first</h2>
            <p className="text-gray-600 leading-relaxed text-sm">
              Keep gambling fun with our help. KoalaBet.win is dedicated to promoting safe and responsible gambling. We partner with international organizations to ensure you have the resources to stay in control. Hover over the logos below to learn more about the regulators and testing agencies protecting you.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-10">
            {safetyLogos.map((item) => (
              <div key={item.name} className="group relative flex items-center cursor-pointer">
                <div className="flex items-center gap-1">
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="h-9 w-auto max-w-[150px] object-contain"
                  />
                  <span className="text-gray-400 text-xs">°</span>
                </div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center">
                  {item.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Count on us to keep you safe */}
      <section
        className="py-12 sm:py-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #7b1a1a 0%, #a02020 40%, #8b1c1c 100%)' }}
      >
        {/* subtle background texture overlay */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, #fff 0%, transparent 60%)' }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
            {/* Left */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Count on us to keep you safe</h2>
              <p className="text-red-200 text-sm leading-relaxed mb-8">
                If a casino doesn't meet our high standards, it won't make it to our recommendations — no exceptions. Our in-depth reviewing process uncovers unsafe casinos, steering you clear of sites that could risk your time or money. We've got your back, every step of the way.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
                <div className="bg-white rounded-lg p-3.5 sm:p-4">
                  <div className="text-red-600 font-bold text-xl sm:text-2xl mb-1">67</div>
                  <div className="text-gray-600 text-xs sm:text-sm leading-snug">untrustworthy casinos identified</div>
                </div>
                <div className="bg-white rounded-lg p-3.5 sm:p-4">
                  <div className="text-red-600 font-bold text-xl sm:text-2xl mb-1">3,300+</div>
                  <div className="text-gray-600 text-xs sm:text-sm leading-snug">player complaints investigated</div>
                </div>
                <div className="bg-white rounded-lg p-3.5 sm:p-4 col-span-2 sm:col-span-1 sm:max-w-none max-w-[260px] mx-auto w-full">
                  <div className="text-red-600 font-bold text-xl sm:text-2xl mb-1">350+</div>
                  <div className="text-gray-600 text-xs sm:text-sm leading-snug">dishonest terms found</div>
                </div>
              </div>
            </div>

            {/* Right — Blacklisted casino card */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-80">
                {/* Ban icon overlay */}
                <div className="absolute -top-4 -right-4 z-20 w-20 h-20 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full border-4 border-red-400 bg-red-100/20 flex items-center justify-center">
                    <i className="ri-forbid-2-line text-red-300 text-4xl"></i>
                  </div>
                </div>

                <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
                  {/* Casino name header */}
                  <div className="px-6 py-4 border-b border-gray-100 text-center">
                    <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">Winzau69 Casino</span>
                  </div>

                  {/* Rating */}
                  <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-center gap-2">
                    <span className="text-sm text-gray-600">Expert Rating</span>
                    <i className="ri-star-fill text-orange-400 text-sm"></i>
                    <span className="font-bold text-gray-900 text-sm">1.5/5</span>
                  </div>

                  {/* Issues */}
                  <div className="px-6 py-5 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-close-circle-fill text-red-500 text-lg"></i>
                      </div>
                      <span className="text-sm text-gray-700">Unclear terms and conditions</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-close-circle-fill text-red-500 text-lg"></i>
                      </div>
                      <span className="text-sm text-gray-700">Winnings unpaid for over 3 months</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-close-circle-fill text-red-500 text-lg"></i>
                      </div>
                      <span className="text-sm text-gray-700">Misleading licensing information</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
